import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPermissions, ALL_PERMISSION_TYPES } from '../services/Login';
import ExpiredAccountError from '../schemas/Exception/ExpiredAccountError';


interface PermissionsContextType {
  permissions: Record<string, boolean>;
  isAccountExpired: boolean;
  loading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
  hasPermission: (permissionType: string) => boolean;
}

// Create the context with a default value
const PermissionsContext = createContext<PermissionsContextType>({
  permissions: {},
  isAccountExpired: false,
  loading: true,
  error: null,
  refreshPermissions: async () => {},
  hasPermission: () => false
});

// Custom hook to use the permissions context
export const usePermissions = () => useContext(PermissionsContext);

// Provider component
export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isAccountExpired, setAccountExpired] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch permissions
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPermissions();

      let formattedPermissions: Record<string, boolean> = {};
      
      if (Array.isArray(data)) {
        // If data is an array of permission strings
        ALL_PERMISSION_TYPES.forEach(permType => {
          formattedPermissions[permType] = data.includes(permType);
        });
      } else if (typeof data === 'object' && data !== null) {
        // If data is already an object with boolean values
        formattedPermissions = { ...data };
      }
      
      setPermissions(formattedPermissions);
    } catch (err) {
      if (err instanceof ExpiredAccountError) {
        setAccountExpired(true);
        return;
      }

      console.error('Error fetching permissions:', err);
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  // Function to check if a specific permission exists
  const hasPermission = (permissionType: string): boolean => {
    return Boolean(permissions[permissionType]);
  };

  // Fetch permissions on mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        isAccountExpired,
        loading,
        error,
        refreshPermissions: fetchPermissions,
        hasPermission
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};